import { FaFacebook, FaFacebookMessenger } from 'react-icons/fa6';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { CheckGlyph, delay, tone } from '../../primitives';

/**
 * Facebook's window for choosing the Pages the agent answers for, and the
 * agent's own list of connected Pages, each with its own switch.
 */
export default function MessengerSetup({
  brand,
  otherPage,
}: {
  brand: DemoBrand;
  /** A second Page the owner manages but doesn't connect. */
  otherPage: string;
}) {
  const { t } = useChannelsT();

  const pages = [
    { name: brand.name, initial: brand.initial, bg: brand.bg, picked: true },
    {
      name: otherPage,
      initial: otherPage.charAt(0),
      bg: 'bg-[#64748b]',
      picked: false,
    },
  ];

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-24'>
      <div className='overflow-hidden rounded-[22px] bg-white text-start text-[#050505] shadow-[0_40px_90px_-35px_rgba(30,20,90,0.55)] ring-1 ring-black/5 dark:bg-[#242526] dark:text-[#e4e6eb] dark:ring-white/10'>
        <div className='flex items-center gap-2 bg-[#0866ff] px-5 py-3 text-white'>
          <FaFacebook className='h-5 w-5' />
          <span className='text-sm font-semibold'>Facebook</span>
        </div>
        <div className='px-5 py-5'>
          <p className='text-[15px] font-semibold'>
            {t('Choose the Pages your agent answers for')}
          </p>
          <p className='mt-1 text-[13px] text-[#65676b] dark:text-[#b0b3b8]'>
            {t('Easy-Peasy.AI will reply to messages sent to these Pages.')}
          </p>
          <ul className='mt-4 space-y-2'>
            {pages.map(({ name, initial, bg, picked }) => (
              <li
                key={name}
                className='flex items-center gap-3 rounded-xl px-3 py-2.5 ring-1 ring-inset ring-black/10 dark:ring-white/10'
              >
                <span
                  className={clsxm(
                    'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold text-white',
                    bg,
                  )}
                >
                  {initial}
                </span>
                <span className='min-w-0 flex-1 truncate text-[14px] font-medium'>
                  {name}
                </span>
                <span
                  className={clsxm(
                    'flex h-5 w-5 flex-none items-center justify-center rounded-md',
                    picked
                      ? 'bg-[#0866ff] text-white'
                      : 'ring-1 ring-inset ring-black/25 dark:ring-white/30',
                  )}
                >
                  {picked && <CheckGlyph className='h-3.5 w-3.5' />}
                </span>
              </li>
            ))}
          </ul>
          <span className='mt-5 flex w-full items-center justify-center rounded-lg bg-[#0866ff] px-4 py-2.5 text-sm font-semibold text-white'>
            {t('Continue')}
          </span>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 rounded-2xl p-4 text-start shadow-[0_30px_60px_-28px_rgba(30,20,90,0.6)] sm:absolute sm:-bottom-2 sm:end-[-1.5rem] sm:mt-0 sm:w-[20rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p
          className={clsxm(
            'flex items-center gap-2 text-[13px] font-semibold',
            tone.ink,
          )}
        >
          <FaFacebookMessenger className='h-4 w-4 text-[#0084FF]' />
          {t('Manage Facebook Pages')}
        </p>
        <div className='mt-3 flex items-center gap-3'>
          <span
            className={clsxm(
              'flex h-8 w-8 flex-none items-center justify-center rounded-full text-[13px] font-semibold text-white',
              brand.bg,
            )}
          >
            {brand.initial}
          </span>
          <span className='min-w-0 flex-1'>
            <span
              className={clsxm(
                'block truncate text-[13px] font-semibold',
                tone.ink,
              )}
            >
              {brand.name}
            </span>
            <span className='mt-0.5 inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
              {t('Active')}
            </span>
          </span>
          <span className='relative h-5 w-9 flex-none rounded-full bg-primary-600'>
            <span className='absolute end-0.5 top-0.5 h-4 w-4 rounded-full bg-white' />
          </span>
        </div>
      </div>
    </div>
  );
}
